/**
 * A sign-out — or a sign-in — in one tab, told to the others in this browser at once (#961, #1316).
 *
 * The drive (`useAuth.signOut`) and the share view's Log out both sign the account
 * out. The server then refuses the session on its next request (`ended-sessions.ts`),
 * but a drive tab that makes no request keeps looking signed in. Each tab that
 * signs out announces it here; the drive's tabs re-check their session.
 *
 * #1316: a sign-in replaces the browser's session cookie WITHOUT signing the previous
 * account out — no `signed-out` message, and the old account's tabs get 403s (the new
 * account's cookie is valid), never the 401 the session-death check reacts to. So the
 * tab whose session became an account it did not hold before announces `signed-in` on
 * the same channel; the others re-check, and one now holding another account reloads
 * (`useAuth` `setSessionUser`).
 *
 * Carries no data beyond the event and an opaque id (so the sending tab can skip its
 * own message): which account it was doesn't matter, as the re-check asks the server.
 */
export const SIGN_OUT_CHANNEL = 'papan-sign-out'

type AccountEvent = 'signed-out' | 'signed-in'
interface AccountMessage { type: AccountEvent; id?: string }

const isEvent = (data: unknown, type: AccountEvent): data is AccountMessage =>
  typeof data === 'object' && data !== null && (data as { type?: unknown }).type === type

/** Ids this tab announced: a channel delivers to every OTHER channel object, this tab's
 *  own listeners included — the tab that signed out is already leaving, and the tab that
 *  signed in already knows its account. */
const sentHere = new Set<string>()

function announce(type: AccountEvent): void {
  if (typeof BroadcastChannel === 'undefined') return
  try {
    const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Math.random())
    sentHere.add(id)
    const channel = new BroadcastChannel(SIGN_OUT_CHANNEL)
    channel.postMessage({ type, id } satisfies AccountMessage)
    channel.close()
  } catch { /* best-effort: the server still refuses the session */ }
}

function onElsewhere(type: AccountEvent, handler: () => void): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {}
  const channel = new BroadcastChannel(SIGN_OUT_CHANNEL)
  channel.onmessage = (event) => {
    if (!isEvent(event.data, type)) return
    if (event.data.id && sentHere.has(event.data.id)) return
    handler()
  }
  return () => channel.close()
}

/** Tell this browser's other tabs that the account was signed out. Never throws. */
export function announceSignedOut(): void { announce('signed-out') }

/** Call `onSignedOut` when another tab of this browser signs out. Returns the unsubscribe. */
export function onSignedOutElsewhere(onSignedOut: () => void): () => void { return onElsewhere('signed-out', onSignedOut) }

/** #1316 — tell this browser's other tabs that this tab's session is now an account it did not hold before (any
 *  sign-in: they all end in `useAuth.refreshSession`, an OAuth or share-view sign-in on the page load it returns to).
 *  Never throws. */
export function announceSignedIn(): void { announce('signed-in') }

/** #1316 — call `onSignedIn` when another tab of this browser signs an account in. Returns the unsubscribe. */
export function onSignedInElsewhere(onSignedIn: () => void): () => void { return onElsewhere('signed-in', onSignedIn) }
